import _ from 'lodash';
import { RBT } from './rbt';
import { NotifySettings } from '@/types';
import { DEFAULT_NOTIFICATION_TONE_PREFIX } from '@/constants';
import { getDiscoveryUrl } from '@/common';

const getDTMFSounds = async () => {
  return {
    '0': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-0.mp3'),
    '1': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-1.mp3'),
    '2': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-2.mp3'),
    '3': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-3.mp3'),
    '4': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-4.mp3'),
    '5': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-5.mp3'),
    '6': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-6.mp3'),
    '7': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-7.mp3'),
    '8': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-8.mp3'),
    '9': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-9.mp3'),
    '#': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-pound.mp3'),
    '*': await import(/* webpackChunkName: "static" */ '@/../static/sound/dtmf/DTMF-star.mp3'),
  };
};

export class Ringtone {
  private static LENGTH = 1;
  private static PAUSE = 1.5;

  private static interval: number | undefined;
  private static sounds: Record<string, HTMLAudioElement> = {};
  private static isEnabled: Record<string, boolean> = {};
  private static playOnBlur: Record<string, boolean> = {};

  private static isDebug = window.localStorage.getItem('HITL-Debug') === 'true';

  private static isAudioPlaying = (audio: HTMLMediaElement) => {
    return audio &&
      audio.currentTime > 0 &&
      !audio.paused &&
      !audio.ended &&
      audio.readyState > 2;
  };

  public static async init(args: {
    notifications: NotifySettings;
    humanInTheLoopURL?: string;
    serviceDiscoveryURL?: string;
  }) {
    const { notifications, humanInTheLoopURL, serviceDiscoveryURL } = args;
    const rootURL = humanInTheLoopURL
      ? humanInTheLoopURL
      : await getDiscoveryUrl(serviceDiscoveryURL!);

    const getLink = (originalLink: string) => {
      return originalLink.startsWith(DEFAULT_NOTIFICATION_TONE_PREFIX)
        ? `${rootURL}${originalLink}`
        : originalLink;
    };

    _.forOwn(notifications, (action, key) => {
      const link = getLink(action!.link);
      this.sounds[key] = new Audio(link);
      this.isEnabled[key] = action!.isEnabled;
      this.playOnBlur[key] = action!.onBlur || false;
    });

    const DTMF_SOUNDS: Record<string, string> = await getDTMFSounds();
    _.forEach([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, '#', '*'], tone => {
      const key = `dtmf-${tone}`;
      this.sounds[key] = new Audio(_.get(DTMF_SOUNDS[tone], ['default']));
      this.isEnabled[key] = true;
    });
  }

  public static play(action: string) {
    if (action === 'rbt') {
      this.debug(action);
      RBT.start();
      return;
    }

    if (!this.isEnabled[action]) {
      return;
    }
    if (this.playOnBlur[action] && document.hasFocus()) {
      return;
    }

    if (action === 'call') {
      this.debug(action);
      const call = this.sounds.call.play.bind(this.sounds.call);
      call();

      const timer = (this.LENGTH + this.PAUSE) * 1000;
      this.interval = window.setInterval(call, timer);
    } else {
      this.debug(action);
      this.sounds[action].play();
    }
  }

  public static stop() {
    if (this.isDebug) {
      console.warn('Ringtone API - Stop');
    }

    const isCallSoundPlaying = this.isAudioPlaying(this.sounds.call);
    if (isCallSoundPlaying) {
      this.sounds.call.pause();
      this.sounds.call.currentTime = 0;
    }

    RBT.stop();
    clearInterval(this.interval);
  }

  private static debug(action: string) {
    if (this.isDebug) {
      console.warn(`Ringtone API - [${action}]`);
    }
  }
}
